import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApplicationFormService } from '../../services/application-form/application-form.service';
import { ApplicationForm, FormFieldConfig } from '../../interfaces/responses/application-form.interface';

@Component({
  selector: 'app-apply-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './apply-form.component.html',
  styleUrl: './apply-form.component.scss',
})
export class ApplyFormComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _fb = inject(FormBuilder);
  private readonly _formService = inject(ApplicationFormService);

  readonly isLoading = signal(true);
  readonly isInvalid = signal(false);
  readonly isSubmitting = signal(false);
  readonly isSubmitted = signal(false);
  readonly submitError = signal<string | null>(null);

  readonly formConfig = signal<ApplicationForm | null>(null);
  readonly fields = signal<FormFieldConfig[]>([]);

  token = '';
  dynamicForm!: FormGroup;

  ngOnInit(): void {
    this.token = this._route.snapshot.paramMap.get('token') ?? '';
    this._formService.getFormByToken(this.token).subscribe({
      next: (config) => {
        this.formConfig.set(config);
        this.fields.set(config.fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        this.buildForm(this.fields());
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('[ApplyForm] getFormByToken failed', { token: this.token, status: err?.status, error: err?.error });
        this.isInvalid.set(true);
        this.isLoading.set(false);
      },
    });
  }

  buildForm(fields: FormFieldConfig[]): void {
    const controls: Record<string, FormControl> = {};
    for (const field of fields) {
      controls[field.key] = new FormControl('', field.required ? Validators.required : []);
    }
    this.dynamicForm = this._fb.group(controls);
  }

  getControl(key: string): FormControl {
    return this.dynamicForm.get(key) as FormControl;
  }

  onFileChange(event: Event, key: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.getControl(key).setValue(file);
  }

  isControlInvalid(key: string): boolean {
    const ctrl = this.getControl(key);
    return ctrl.invalid && ctrl.touched;
  }

  submit(): void {
    this.dynamicForm.markAllAsTouched();
    if (this.dynamicForm.invalid) return;

    this.isSubmitting.set(true);
    this.submitError.set(null);

    this._formService.submitForm({
      token: this.token,
      fields: this.dynamicForm.value,
    }).subscribe({
      next: () => {
        this.isSubmitted.set(true);
        this.isSubmitting.set(false);
      },
      error: () => {
        this.submitError.set('Something went wrong. Please try again.');
        this.isSubmitting.set(false);
      },
    });
  }
}
